import React, { useRef, useState } from "react";
import { Card, Modal, Button, Dropdown, Menu, Table } from "antd";
import { Bar, Pie } from "react-chartjs-2";
import {
  EllipsisOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

const ChartCard = ({
  title,
  data,
  chartType = "bar",
  showAsPercentage = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const chartRef = useRef(null);

  // Función para descargar la imagen del gráfico
  const downloadChart = () => {
    if (chartRef.current) {
      const imageURL = chartRef.current.toBase64Image();
      const link = document.createElement("a");
      link.href = imageURL;
      link.download = `${title}.png`;
      link.click();
    }
  };
  // Menú de opciones (Ver y Descargar)
  const menuItems = [
    {
      key: "view",
      label: (
        <span onClick={() => setIsModalOpen(true)}>
          <EyeOutlined style={{ fontSize: "18px", marginRight: "8px" }} />
          Ver Gráfica
        </span>
      ),
    },
    {
      key: "download",
      label: (
        <span onClick={downloadChart}>
          <DownloadOutlined style={{ fontSize: "18px", marginRight: "8px" }} />
          Descargar
        </span>
      ),
    },
    {
      key: "table",
      label: (
        <span onClick={() => setIsTableOpen(true)}>
          <EyeOutlined style={{ fontSize: "18px", marginRight: "8px" }} />
          Ver Tabla
        </span>
      ),
    },
  ];

  const isStacked = data.datasets?.some((ds) => ds.stack); // Detecta si hay apilamiento

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: title,
        font: { size: 18, weight: "bold" },
        color: "#0B3360",
        padding: { top: 5, bottom: 30 },
      },
      legend: {
        display: true,
        position: "bottom", // 📌 Coloca la leyenda debajo de la gráfica
        labels: {
          font: { size: 14, weight: "bold" },
          padding: 10,
        },
      },
      datalabels: {
        display: true,
        color: "#000",
        font: { weight: "bold", size: 16 },
        formatter: (value, context) => {
          if (showAsPercentage) {
            const dataset = context.chart.data.datasets[0];
            const total = dataset.data.reduce((acc, val) => acc + val, 0);
            const percentage = (value / total) * 100;
            return `${percentage.toFixed(1)}%`;
          } else {
            return value;
          }
        },
        anchor:
          chartType === "bar"
            ? isStacked
              ? "center"
              : "end" // 📌 Si es apilado, datos dentro de la barra
            : "center",
        align:
          chartType === "bar"
            ? isStacked
              ? "center"
              : "top" // 📌 Si es apilado, centra el texto
            : "center",
      },
    },
    scales:
      chartType === "bar"
        ? {
            x: {
              stacked: isStacked, // 📌 Detecta si la gráfica es apilada
              ticks: { autoSkip: false },
              grid: { display: false },
            },
            y: {
              stacked: isStacked, // 📌 Activa acumulación en el eje Y si es apilado
              beginAtZero: true,
              grid: { color: "rgba(0, 0, 0, 0.1)" },
            },
          }
        : {}, // Si es de torta, no usa escalas
  };

  return (
    <Card
      style={{
        position: "relative",
        borderRadius: 20,
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(10px)",
        paddingTop: "30px",
        marginTop: "20px",
      }}
    >
      {/* Botón de menú con tres puntos */}
      <div style={{ position: "absolute", top: 15, right: 15 }}>
        <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
          <Button
            shape="circle"
            icon={<EllipsisOutlined style={{ fontSize: "20px" }} />}
          />
        </Dropdown>
      </div>

      {/* Contenedor de la gráfica */}
      <div
       style={{
        width: "100%",
        height: "450px",
        overflowX: "auto", // Habilita scroll horizontal si es necesario
      }}
      >
        {/* 📌 Este div interno tendrá un ancho mínimo igual al contenedor */}
        <div
         style={{
           minWidth: `${data.labels.length * 80}px`, // Ancho mínimo en función de las barras
           height: "400px",
         }}
        >
          {chartType === "bar" ? (
            <Bar ref={chartRef} data={data} options={chartOptions} />
          ) : (
            <Pie ref={chartRef} data={data} options={chartOptions} />
          )}
        </div>
      </div>

      {/* Modal con la gráfica ampliada */}
      <Modal
        title={title}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={1000}
        style={{ maxWidth: "90vw" }}
      >
        {/* Contenedor de la gráfica en el modal */}
        <div
          style={{
            width: "100%", // 📌 Ocupar el ancho del contenedor padre
            height: "500px",
            display: "flex",
            justifyContent: "center",
            overflowX: "auto", // 📌 Habilita el scroll si es necesario
          }}
        >
          {/* 📌 Este div interno ajusta el ancho mínimo para habilitar el scroll */}
          <div
            style={{
              minWidth: chartType === "bar" ? "max(100%, 800px)" : "100%",
              height: "500px",
            }}
          >
            {chartType === "bar" ? (
              <Bar
                data={data}
                options={{ ...chartOptions, maintainAspectRatio: false }}
              />
            ) : (
              <Pie
                data={data}
                options={{ ...chartOptions, maintainAspectRatio: false }}
              />
            )}
          </div>
        </div>
      </Modal>
      {/* Modal con la tabla de datos */}
      <Modal
        title={`Datos de la gráfica: ${title}`}
        open={isTableOpen}
        onCancel={() => setIsTableOpen(false)}
        footer={null}
        width={700}
      >
        <Table
          columns={[
            { title: data.labels && data.labels.length > 0 ? 'Categoría' : 'Etiqueta', dataIndex: 'label', key: 'label' },
            ...data.datasets.map((ds, i) => ({
              title: ds.label || `Serie ${i + 1}`,
              dataIndex: `value${i}`,
              key: `value${i}`,
            })),
          ]}
          dataSource={
            data.labels
              ? data.labels.map((label, idx) => {
                  const row = { key: idx, label };
                  data.datasets.forEach((ds, i) => {
                    row[`value${i}`] = ds.data[idx];
                  });
                  return row;
                })
              : []
          }
          pagination={false}
          bordered
          size="small"
        />
      </Modal>
    </Card>
  );
};

export default ChartCard;
